/* Copyright (c) 2020-present CloudIO Inc */

import { ItemType } from '../../registry/ComponentRegistry';
import { toOptions } from 'componentregistry/utils/ManagerUtils';
import { Platform } from '../../platform/Platform';
import { ComponentManagerFn } from '../../registry/Component.type';
import { baseComponentManager } from '../../registry/Components';
import { getManager } from '../../registry/Manager';
import { defaultValue } from './Comments';

const CommentsManager: ComponentManagerFn<'Comments'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'Comments'>(platform, 'Comments'),
    isChildAllowed: (page, item, childType) =>
      (
        [
          'CommentActionMenuItems',
          'TriggerOnComment',
          'TriggerOnMention',
        ] as ItemType[]
      ).includes(childType),
    type: 'Comments',
    name: 'Comments',
    defaultValue,
    props: {
      header: {
        label: 'Header',
        type: 'expression',
        returnType: async () => 'string',
        helpText: 'The title to be displayed on the comments drawer.',
      },
      placeholder: {
        label: 'Placeholder',
        type: 'expression',
        returnType: async () => 'string',
        helpText:
          'A placeholder that will be displayed in the comment box before the user starts typing the comments.',
      },
      context: {
        label: 'Context',
        type: 'string',
        placeholder: 'e.g. InvoiceComments',
        helpText:
          'A unique context across your application. e.g. OrderComments, InvoiceComments etc.',
      },
      contextValue: {
        label: 'Context Value',
        type: 'expression',
        helpText: `A value within the above context to seperate out the comments.

You can provide a static text, e.g. "All", if you don't want to seperate the comments.

To have different comments per record or record type, you can provide an expression e.g. \`\${$.alias.invoiceId}\` or \`\${$.alias.invoiceType}\``,
        returnType: async () => 'string',
      },
      position: {
        label: 'Position',
        type: 'select',
        options: toOptions(['left', 'right']),
        helpText:
          'The position on the screen from which side the comments drawer should be drawn out.',
      },
      open: {
        label: 'Open',
        type: 'boolean',
        helpText:
          'Leave this off to show the comments drawer only when the user clicks the comments Icon. If this is on, then the comments drawer will be displayed as soon as the user navigates to this page.',
      },
    },
    getDisplayText: (item, expanded, { appUid, pageId }) => {
      const page = platform.getPage(appUid, pageId);
      const itemIds = Object.keys(page);
      const usage = itemIds.filter((id) => {
        const itm = page[id];
        const mgr = getManager(itm.type);
        return mgr.isItemIdUsed(itm, item.itemId);
      });
      return usage.length
        ? `${item.props.context} (${usage.length} usage${
            usage.length > 1 ? 's' : ''
          })`
        : item.props.context;
    },
    category: 'Data',
    canHaveChildren: () => false,
    noOverlay: true,
    icon: { iconName: 'comments' },
    description: `**Overview**<br/>
The Comments component adds a fully functional collaboration engine to your application. Instead of cluttering your main UI with inline chat boxes, this component renders a hidden Drawer (side panel) that slides in when triggered.
It is designed to handle threaded discussions attached to specific data records—such as "Invoice Remarks," "Task Notes," or "Product Reviews." It supports real-time feedback, user mentions, and custom workflows.

**1. Context & Data Binding**<br/>
To ensure comments are attached to the correct record, you must define the "Context" of the discussion.
* **Context:** A static string defining the category of the discussion (e.g., "InvoiceComments", "OrderNotes").
* **Context Value:** An expression defining the specific ID of the record. (Example: If you are on an Invoice page, set this to \`inv_\${$.invoiceStore.id}\`. When the user switches to a different invoice, the drawer automatically refreshes to show only comments for that specific invoice).
* **Global Chat:** If you want a shared chat room for the entire page, set this to a static string like "All".

**2. Interaction & Visibility**<br/>
The Comments component stays hidden by default to keep your interface clean.
* **Position:** Determines which side of the screen the drawer slides in from (Left or Right).
* **Open:** A boolean flag. (False is recommended so the drawer stays hidden until triggered; True opens it automatically on load).
* **Header:** The title text displayed at the top of the drawer (e.g., "Team Discussion").
* **Placeholder:** The help text shown in the empty input box (e.g., "Type your feedback here...").

**3. Extensibility (Actions & Triggers)**<br/>
The Comments component is not just a text log; it is an interactive workflow tool.
* **Triggers:**
    * **TriggerOnComment:** Fires logic whenever a new comment is posted (e.g., send an email notification to the record owner).
    * **TriggerOnMention:** Fires logic when a user is @mentioned (e.g., create a task for that user).
* **Custom Actions:** You can add a **Comment Action Menu Items** child component. This allows you to insert custom buttons into the "More Actions" menu of every comment, enabling workflows like "Mark as Resolved" or "Flag as Inappropriate."

**4. How to Trigger the Drawer**<br/>
Since the component is hidden by default, you need a way to open it.
1. Add an **Icon** or **Icon Button** component to your page (e.g., near the Page Header).
2. In the Icon's properties, look for the **"Comments Item ID"** property.
3. Select the Item ID of this Comments component.
* **Result:** Clicking that icon will now toggle the Comments drawer.`,
  };
};

export default CommentsManager;
